import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useMountedState, useUnmount } from 'react-use';
import { BankLogoImage } from 'shared/components';
import { noValue } from 'shared/constants';
import { useGetAmaxPayOpenBankingBankAccounts } from 'shared/data/webApp/v1';
import { useGetXeroBankAccounts } from 'shared/data/webApp/v1';

import { mapMaxPayOpenBankingBankAccount } from '../../helpers/mapMaxPayOpenBankingBankAccount';
import { accountParams } from './components/NewBankAccountCard/NewBankAccountCard.constants';

export const useXeroBankAccounts = (companyId: string, consentId: string, matchedXeroBankAccountIds: string[]) => {
    const { data, isLoading } = useGetXeroBankAccounts({ path: { companyId } }, { enabled: Boolean(consentId) });

    const xeroBankAccounts = useMemo(
        () =>
            (data?.items || []).map((item) => ({
                ...item,
                sortCode: item.sortCode ?? noValue,
                matched: matchedXeroBankAccountIds.includes(item.id),
            })),
        [matchedXeroBankAccountIds, data?.items]
    );

    return {
        xeroBankAccounts,
        isLoadingXeroBankAccounts: isLoading,
    };
};

export const useMatchingPopupData = ({
    companyId,
    consentId,
    bankAccountId,
    reMatch,
}: {
    companyId: string;
    consentId: string;
    bankAccountId: string | undefined;
    reMatch: boolean;
}) => {
    const isMounted = useMountedState();
    const timerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [allowBankAccountPolling, setAllowBankAccountPolling] = useState(true);

    const { data, isLoading, refetch } = useGetAmaxPayOpenBankingBankAccounts(
        { path: { companyId } },
        {
            enabled: Boolean(consentId),
            // we need to poll the query for the case when amaxPay might not
            // return the new BA immediately. This should be the rare case.
            refetchInterval: () => {
                if (!allowBankAccountPolling || reMatch) {
                    return false;
                }

                return 3000;
            },
        }
    );

    const items = useMemo(() => (data?.items || []).map(mapMaxPayOpenBankingBankAccount), [data?.items]);

    const { bankAccounts, matchedXeroBankAccountIds } = useMemo(() => {
        /**
         * The full list of xero accounts for all that been matched with any consent
         */
        const matchedXeroBankAccountIds: string[] = [];
        const bankAccounts: (NonNullable<(typeof items)[number]> & {
            matched: boolean;
            logoImage: ReactElement;
        })[] = [];

        items.forEach((item) => {
            if (item.xeroBankAccount?.id) {
                matchedXeroBankAccountIds.push(item.xeroBankAccount.id);
            }

            const filterByBankAccountId = bankAccountId ? bankAccountId === item.id : true;

            if (item.consentId === consentId && filterByBankAccountId) {
                bankAccounts.push({
                    ...item,
                    ...accountParams.reduce<Partial<typeof item>>((acc, param) => {
                        acc[param] = item[param] ?? undefined;

                        return acc;
                    }, {}),
                    matched: Boolean(item.xeroBankAccount?.id),
                    logoImage: <BankLogoImage src={item.bank.iconUrl} alt={item.bank.name} width={24} height={24} />,
                });
            }
        });

        return { bankAccounts, matchedXeroBankAccountIds };
    }, [bankAccountId, consentId, items]);

    const matchedBankAccounts = useMemo(
        () =>
            bankAccounts.length ? bankAccounts.filter((bankAccount) => Boolean(bankAccount.xeroBankAccount?.id)) : [],
        [bankAccounts]
    );

    const allBankAccountsWereMatched = bankAccounts.length > 0 && matchedBankAccounts.length === bankAccounts.length;
    const someButNotAllBankAccountsWereMatched =
        matchedBankAccounts.length > 0 && matchedBankAccounts.length < bankAccounts.length;

    const { xeroBankAccounts, isLoadingXeroBankAccounts } = useXeroBankAccounts(
        companyId,
        consentId,
        matchedXeroBankAccountIds
    );

    useEffect(() => {
        if (consentId && !reMatch) {
            timerIdRef.current = setTimeout(
                () => {
                    if (isMounted()) {
                        setAllowBankAccountPolling(false);
                    }
                },
                // allow to do polling 3 times
                3 * 3000 + 1000
            );
        } else {
            setAllowBankAccountPolling(true);
        }

        return () => {
            timerIdRef.current && clearTimeout(timerIdRef.current);
        };
    }, [consentId, reMatch, isMounted]);

    useUnmount(() => {
        timerIdRef.current && clearTimeout(timerIdRef.current);
    });

    return {
        bankAccounts,
        isLoading,
        refetchBankAccounts: refetch,
        xeroBankAccounts,
        isLoadingXeroBankAccounts,
        someButNotAllBankAccountsWereMatched,
        allBankAccountsWereMatched,
    };
};
